import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface UserProfile {
  name: string;
  avatar?: string;
  level: number;
  exp: number;
  totalPomodoros: number;
  currentStreak: number;
  longestStreak: number;
}

interface UserState {
  // User Profile
  user: UserProfile;
  
  // Rewards
  donuts: number;
  affinityExp: number;
  affinityLevel: number;
  
  // Nadenade tracking
  lastNadenadeDate: string | null;
  dailyNadenadeCount: number;
  
  // Actions
  updateUser: (user: Partial<UserProfile>) => void;
  addDonut: (amount: number) => void;
  spendDonut: (amount: number) => boolean;
  addAffinityExp: (exp: number) => void;
  tryNadenade: () => { success: boolean; message: string };
  resetDailyNadenade: () => void;
}

const defaultUser: UserProfile = {
  name: 'ユーザー',
  level: 1,
  exp: 0,
  totalPomodoros: 0,
  currentStreak: 0,
  longestStreak: 0,
};

// 好感度レベルの必要経験値テーブル
export const AFFINITY_LEVEL_TABLE = [
  0,     // Lv1
  50,    // Lv2
  100,   // Lv3
  200,   // Lv4
  350,   // Lv5
  500,   // Lv6
  700,   // Lv7
  1000,  // Lv8
  1500,  // Lv9
  2000,  // Lv10
];

// 現在レベル内での経験値進捗率（0〜1）。最大レベルでは常に1
export const getAffinityProgress = (exp: number, level: number): number => {
  const current = AFFINITY_LEVEL_TABLE[level - 1] ?? 0;
  const next = AFFINITY_LEVEL_TABLE[level];
  if (next === undefined) return 1;
  return Math.min(1, Math.max(0, (exp - current) / (next - current)));
};

const calculateAffinityLevel = (exp: number): number => {
  for (let i = AFFINITY_LEVEL_TABLE.length - 1; i >= 0; i--) {
    if (exp >= AFFINITY_LEVEL_TABLE[i]) {
      return i + 1;
    }
  }
  return 1;
};

export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: defaultUser,
      donuts: 10, // 初期値は通常値
      affinityExp: 0,
      affinityLevel: 1,
      lastNadenadeDate: null,
      dailyNadenadeCount: 0,

      // Actions
      updateUser: (userData) =>
        set((state) => ({
          user: { ...state.user, ...userData },
        })),

      addDonut: (amount) =>
        set((state) => ({
          donuts: Math.max(0, state.donuts + amount),
        })),

      spendDonut: (amount) => {
        const currentDonuts = get().donuts;
        if (currentDonuts >= amount) {
          set({ donuts: currentDonuts - amount });
          return true;
        }
        return false;
      },

      addAffinityExp: (exp) =>
        set((state) => {
          const newExp = state.affinityExp + exp;
          const newLevel = calculateAffinityLevel(newExp);
          return {
            affinityExp: newExp,
            affinityLevel: newLevel,
          };
        }),

      tryNadenade: () => {
        const state = get();
        const today = new Date().toDateString();
        
        // 日付が変わっていたらリセット
        if (state.lastNadenadeDate !== today) {
          set({
            lastNadenadeDate: today,
            dailyNadenadeCount: 0,
          });
        }
        
        const currentCount = get().dailyNadenadeCount;
        
        // 1日3回まで
        if (currentCount >= 3) {
          return {
            success: false,
            message: '今日はもうたくさんなでなでしてもらったよ！明日また来てね♪',
          };
        }
        
        // なでなで成功
        set((state) => ({
          dailyNadenadeCount: state.dailyNadenadeCount + 1,
          affinityExp: state.affinityExp + 5,
          affinityLevel: calculateAffinityLevel(state.affinityExp + 5),
        }));
        
        return {
          success: true,
          message: 'えへへ、嬉しい♪',
        };
      },

      resetDailyNadenade: () => {
        set({
          lastNadenadeDate: null,
          dailyNadenadeCount: 0,
        });
      },
    }),
    {
      name: 'sorarium-user-storage',
      partialize: (state) => ({
        user: state.user,
        donuts: state.donuts,
        affinityExp: state.affinityExp,
        affinityLevel: state.affinityLevel,
        lastNadenadeDate: state.lastNadenadeDate,
        dailyNadenadeCount: state.dailyNadenadeCount,
      }),
    }
  )
);